<template>
  <div class="w-full h-full">
    <div class="w-full h-full flex 2xl:pe-52" v-if="dataLoaded">
      <div class="w-full h-full overflow-y-hidden md:pt-4 md:ml-6">
        <div  class="flex flex-col justify-start items-start h-full w-full px-4 min-h-full gap-3">
          <div class="flex justify-start items-center w-full gap-2">
            <profile-header
              :customer="clientData"
              :subTitle="profileSubTitle"
            />
            <Button v-if="activeTab === 'appointments' && !isMobile" label="זימון תור" class="w-max flex-none" @click="addManualClientAppointment"/>
          </div>

          <TabsComponent
            v-model="activeTab"
            :tabs="tabs"
            class="mb-4"
          />

          <CustomerInformation v-if="activeTab === 'information'" :customer="clientData" @updated="fetchData" />
          <CustomerPaymentHistory v-if="activeTab === 'payments'" :clientId="clientId" @updated="fetchData" />
          <CustomerConsentForms v-if="activeTab === 'consent-forms'" :customer="clientData" />
          <CustomerFiles v-if="activeTab === 'files'" :customer="clientData" @updated="fetchData" />
          <customers-appointment-timeline v-if="activeTab === 'appointments'" :customer="clientData" />
          <customer-loyalty-program v-if="activeTab === 'loyalty-program'" :customer="clientData" @updated="fetchData" />
          <CustomerSkinScans v-if="activeTab === 'skin-scan'" :customer="clientData" />
          <customer-reviews v-if="activeTab === 'reviews'" :customer="clientData" />
          <CustomerPacksAndVouchers v-if="activeTab === 'packs-vouchers'" :customer="clientData" />
          <CustomerMessages v-if="activeTab === 'messages'" :customer="clientData" />
          <CustomerTasks v-if="activeTab === 'tasks'" :customer="clientData" />

        </div>
      </div>
      <!-- Bounded to the viewport (minus the equal top/bottom margins) so the card keeps its
           rounded border on every edge and CustomerInformation scrolls inside it, instead of
           growing past the fold and getting cut off. -->
      <div v-if="!isMobile" class="max-w-[480px] my-4 w-full p-4 rounded-lg border h-[calc(100%-2rem)] overflow-hidden bg-white">
        <CustomerInformation v-if="!isMobile" :customer="clientData" @updated="fetchData"  />
      </div>
    </div>
    <retry-loader
      v-else
      :isErrorState="isErrorState"
      @retry-function="fetchData"
    />
    <ConfirmDialog />
  </div>
</template>

<script lang="ts" setup>
import { type ICustomer, ClientFeatures } from '@torim-app/types';
import { computed, onMounted, ref, watch } from 'vue';
import { CustomersApi } from '@app/api/admin/customers-api';
import ConfirmDialog from 'primevue/confirmdialog';
import { AdminRouteNames } from '@app/router/route-names';
import { useRoute, useRouter } from 'vue-router';
import { TabsComponent, useDrawer } from '@torim-app/shared-components';
import { useAdminClientsStore, useAdminStore } from '@app/stores';
import { useResponsive } from '@app/composables/responsive';
import { useNavigation } from '@app/composables/navigation';
import NewCustomerEvent from '@app/components/admin/modals/NewCustomerEvent.vue';
import CustomerPacksAndVouchers from '@app/components/admin/pages/customer/CustomerPacksAndVouchers.vue';
import CustomerSkinScans from '@app/components/admin/pages/customer/CustomerSkinScans.vue';
import CustomerMessages from '@app/components/admin/pages/customer/CustomerMessages.vue';
import CustomerTasks from '@app/components/admin/pages/tasks/CustomerTasks.vue';
import { trackEvent } from '@app/composables/posthog';

type Tabs = 'information' | 'appointments' | 'files' | 'payments' | 'loyalty-program' | 'reviews' | 'packs-vouchers' | 'consent-forms' | 'skin-scan' | 'messages' | 'tasks';

const adminStore = useAdminStore();
const adminClientsStore = useAdminClientsStore();

const route = useRoute();
const router = useRouter();
const { openDrawer, closeDrawer } = useDrawer();
const { isMobile } = useResponsive();
const {goTo} = useNavigation();

const clientId = route.params.id as string;
if (!clientId) router.push({ name: AdminRouteNames.Clients });

adminStore.setPage(AdminRouteNames.ClientProfile);

const dataLoaded = ref(false);
const isErrorState = ref(false);
const clientData = ref<ICustomer>();
const activeTab = ref<Tabs>(route.query.apid || !isMobile.value ? 'appointments' : 'information');

const skinScanEnabled = computed(
  () => adminStore.getBusinessSiteData.featuresConfigurations?.[ClientFeatures.SkinAnalysis]?.enabled === true,
);

const tabs = computed<Array<{ label: string; value: Tabs }>>(() => {
  const _tabs: Array<{ label: string; value: Tabs } | null> = [
    isMobile.value ? { label: 'אודות', value: 'information' } : null,
    { label: 'תורים', value: 'appointments' },
    { label: 'משימות', value: 'tasks' },
    { label: 'כרטיסיות ושוברים', value: 'packs-vouchers' },
    { label: 'מועדון לקוחות', value: 'loyalty-program' },
    { label: 'משובים', value: 'reviews' },
    { label: 'תשלומים', value: 'payments' },
    { label: 'הודעות', value: 'messages' },
    { label: 'טפסים', value: 'consent-forms' },
    skinScanEnabled.value ? { label: 'טיפול פנים', value: 'skin-scan' } : null,
    { label: 'קבצים', value: 'files' }
  ];

  return _tabs.filter(Boolean) as Array<{ label: string; value: Tabs }>;
});

const profileSubTitle = computed(() => {
  const textsToDisplay: Array<string> = [
    `לקוח/ה מאז ${clientData.value?.customerSince}`
  ];
  if (clientData.value?.smsOptedOut)
    textsToDisplay.push('לא מקבל/ת הודעות');

  return textsToDisplay.join(' ○ ');
});

const fetchData = () => {
  isErrorState.value = false;
  CustomersApi.fetchCustomer(clientId)
    .then(customer => {
      clientData.value = customer;
      dataLoaded.value = true;
    })
    .catch((err) => {
      console.error(err);
      isErrorState.value = true;
    });
};

const addManualClientAppointment = () => {
  trackEvent('customer_profile_schedule_appointment_clicked');
  openDrawer(NewCustomerEvent, {
    onClose: closeDrawer
  }, {
    title: 'זימון תור',
    closable: true,
    noPadding: true
  });
};

watch(activeTab, (newTab) => {
  trackEvent('customer_profile_tab_switched', { tab: newTab });
});

watch(() => route.query.apid, (newValue, oldValue) => {
  if (route.query.apid && !oldValue)
    activeTab.value = 'appointments';
});

onMounted(() => {
  trackEvent('customer_profile_viewed');
  if (adminClientsStore.getClients.length === 0)
    adminClientsStore.fetchBusinessCustomers();

  fetchData();
});

</script>


